import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, ArrowLeft, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CampaignResponses = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingResponse, setEditingResponse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    text: '',
    options: ['', '', ''],
    conditional_text: '',
    is_default: false,
    fila_espera: false,
  });

  useEffect(() => {
    fetchCampaign();
    fetchResponses();
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`);
      setCampaign(response.data);
    } catch (error) {
      toast.error('Erro ao carregar campanha');
      navigate('/responses');
    }
  };

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}/responses`);
      setResponses(response.data);
    } catch (error) {
      toast.error('Erro ao carregar respostas');
    }
    setLoading(false);
  };

  const handleAddResponse = () => {
    setEditingResponse(null);
    setFormData({
      text: '',
      options: ['', '', ''],
      conditional_text: '',
      is_default: false,
      fila_espera: false,
    });
    setDialogOpen(true);
  };

  const handleEditResponse = (response) => {
    setEditingResponse(response);
    setFormData({
      text: response.text,
      options: [...response.options, '', '', ''].slice(0, 8),
      conditional_text: response.conditional_text || '',
      is_default: response.is_default || false,
      fila_espera: response.fila_espera || false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const filteredOptions = formData.options.filter(opt => opt.trim() !== '');
    
    if (filteredOptions.length === 0) {
      toast.error('Adicione pelo menos uma opção');
      return;
    }
    
    if (filteredOptions.length > 8) {
      toast.error('Máximo de 8 opções permitido');
      return;
    }
    
    // Validar condicional apenas se não for resposta padrão
    if (!formData.is_default && !formData.conditional_text) {
      toast.error('Texto condicional é obrigatório para respostas não padrão');
      return;
    }
    
    const payload = {
      text: formData.text,
      options: filteredOptions,
      is_default: formData.is_default,
      conditional_text: formData.is_default ? null : formData.conditional_text,
      fila_espera: formData.fila_espera,
    };
    
    try {
      if (editingResponse) {
        await axios.put(`${API}/responses/${editingResponse.id}`, payload);
        toast.success('Resposta atualizada!');
      } else {
        await axios.post(`${API}/campaigns/${campaignId}/responses`, payload);
        toast.success('Resposta criada!');
      }
      setDialogOpen(false);
      fetchResponses();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao salvar resposta');
    }
  };

  const handleDelete = async (responseId) => {
    if (!window.confirm('Deseja realmente excluir esta resposta?')) return;
    
    try {
      await axios.delete(`${API}/responses/${responseId}`);
      toast.success('Resposta excluída!');
      fetchResponses();
    } catch (error) {
      toast.error('Erro ao excluir resposta');
    }
  };

  const addOptionField = () => {
    if (formData.options.length < 8) {
      setFormData({ ...formData, options: [...formData.options, ''] });
    }
  };

  const removeOptionField = (index) => {
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const updateOption = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData({ ...formData, options: newOptions });
  };

  // Uma campanha pode ter apenas uma resposta padrao
  const existingDefault = responses.find((response) => response.is_default);
  const defaultTakenByAnother =
    existingDefault && (!editingResponse || editingResponse.id !== existingDefault.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/responses')}
          className="mb-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para Campanhas
        </Button>
        
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {campaign?.name} - Respostas
        </h1>
        <p className="text-gray-600">Gerencie as respostas automáticas desta campanha</p>
      </div>

      {/* Add Response Button */}
      <div className="mb-6">
        <Button
          onClick={handleAddResponse}
          className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Resposta
        </Button>
      </div>

      {/* Responses List */}
      {responses.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">Nenhuma resposta cadastrada para esta campanha</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {responses.map((response) => (
            <Card key={response.id} className="border-2 hover:border-emerald-300 transition-colors">
              <CardContent className="pt-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {/* Header com badges */}
                    <div className="flex gap-2 mb-4">
                      {response.is_default && (
                        <Badge className="bg-yellow-500 text-white">
                          <Star className="w-3 h-3 mr-1" />
                          Padrão
                        </Badge>
                      )}
                      {response.fila_espera && (
                        <Badge className="bg-purple-500 text-white">
                          Fila de Espera
                        </Badge>
                      )}
                    </div>

                    {/* Texto da resposta */}
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Texto da Resposta:</p>
                      <p className="text-sm text-gray-900">{response.text}</p>
                    </div>
                    
                    {/* Condicional */}
                    {response.conditional_text && (
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Condição:</p>
                        <Badge variant="outline" className="text-xs">
                          {response.conditional_text}
                        </Badge>
                      </div>
                    )}
                    
                    {/* Opções */}
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">Opções:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {response.options.map((option, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-800">
                              {idx + 1}
                            </Badge>
                            <span className="text-sm">{option}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditResponse(response)}
                      className="hover:bg-emerald-50"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(response.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog para Adicionar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingResponse ? 'Editar Resposta' : 'Adicionar Nova Resposta'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Switch Resposta Padrão */}
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5 text-yellow-600" />
                <div>
                  <Label htmlFor="is_default" className="font-semibold">Resposta Padrão</Label>
                  <p className="text-xs text-gray-600">
                    {defaultTakenByAnother
                      ? 'Esta campanha já possui uma resposta padrão.'
                      : 'Não requer texto condicional. Apenas uma por campanha.'}
                  </p>
                </div>
              </div>
              <Switch
                id="is_default"
                checked={formData.is_default}
                disabled={defaultTakenByAnother}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
              />
            </div>

            {/* Switch Fila de Espera */}
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div>
                <Label htmlFor="fila_espera" className="font-semibold">Fila de Espera</Label>
                <p className="text-xs text-gray-600">Marcar esta resposta como fila de espera</p>
              </div>
              <Switch
                id="fila_espera"
                checked={formData.fila_espera}
                onCheckedChange={(checked) => setFormData({ ...formData, fila_espera: checked })}
              />
            </div>

            <div>
              <Label htmlFor="text">Texto da Resposta</Label>
              <Textarea
                id="text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                placeholder="Digite o texto da resposta automática..."
                rows={3}
                required
              />
            </div>

            {/* Condicional (oculto se for resposta padrão) */}
            {!formData.is_default && (
              <div>
                <Label htmlFor="conditional_text">Texto Condicional (obrigatório)</Label>
                <Input
                  id="conditional_text"
                  value={formData.conditional_text}
                  onChange={(e) => setFormData({ ...formData, conditional_text: e.target.value })}
                  placeholder="Ex: quando mencionarem 'preço'"
                  required={!formData.is_default}
                />
              </div>
            )}

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Opções de Resposta (1-8)</Label>
                {formData.options.length < 8 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addOptionField}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Adicionar Opção
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Opção ${index + 1}`}
                    />
                    {formData.options.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeOptionField(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
            >
              {editingResponse ? 'Atualizar Resposta' : 'Criar Resposta'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignResponses;
